import React, { useState } from 'react';
import { 
  User, 
  Settings, 
  Shield, 
  Bell, 
  HelpCircle, 
  LogOut, 
  Edit, 
  Camera,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Star,
  Award,
  FileText,
  Users,
  Heart,
  PawPrint,
  Plane,
  Building2,
  Wallet,
  BookOpen
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';

interface ProfileSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  count?: number;
}

export function MobileProfile() {
  const { user, profile, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const profileSections: ProfileSection[] = [
    {
      id: 'personal',
      title: 'Personal Info',
      icon: <User size={20} />,
      description: 'Manage your personal information',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 'security',
      title: 'Security',
      icon: <Shield size={20} />,
      description: 'Password and security settings',
      color: 'bg-red-100 text-red-600'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <Bell size={20} />,
      description: 'Manage your notifications',
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'help',
      title: 'Help & Support',
      icon: <HelpCircle size={20} />,
      description: 'Get help and contact support',
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  const quickStats = [
    {
      label: 'iDecide Score',
      value: profile?.idecide_score || 0,
      max: 500,
      icon: <Star size={16} />,
      color: 'text-yellow-600'
    },
    {
      label: 'Files Stored',
      value: 24,
      icon: <FileText size={16} />,
      color: 'text-blue-600'
    },
    {
      label: 'Family Members',
      value: 3,
      icon: <Users size={16} />,
      color: 'text-green-600'
    },
    {
      label: 'Active Modules',
      value: 8,
      icon: <Award size={16} />,
      color: 'text-purple-600'
    }
  ];

  const moduleStats = [
    { name: 'Health', icon: <Heart size={16} />, color: 'bg-red-100 text-red-600', count: 12 },
    { name: 'Pets', icon: <PawPrint size={16} />, color: 'bg-orange-100 text-orange-600', count: 5 },
    { name: 'Travel', icon: <Plane size={16} />, color: 'bg-blue-100 text-blue-600', count: 3 },
    { name: 'Legal', icon: <Building2 size={16} />, color: 'bg-gray-100 text-gray-600', count: 8 },
    { name: 'Financial', icon: <Wallet size={16} />, color: 'bg-green-100 text-green-600', count: 15 },
    { name: 'Legacy', icon: <BookOpen size={16} />, color: 'bg-indigo-100 text-indigo-600', count: 2 }
  ];

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      signOut();
    }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Profile Header */}
      <div className="text-center">
        <div className="relative inline-block mb-4">
          <div className="w-24 h-24 bg-idecide-primary rounded-full flex items-center justify-center">
            {profile?.avatar_url ? (
              <img 
                src={profile.avatar_url} 
                alt="Profile" 
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <User size={48} className="text-idecide-dark" />
            )}
          </div>
          <button className="absolute bottom-0 right-0 bg-idecide-primary p-2 rounded-full border-2 border-white">
            <Camera size={16} className="text-idecide-dark" />
          </button>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900">
          {profile?.full_name || 'User Name'}
        </h2>
        <p className="text-sm text-gray-600">{user?.email}</p>
        
        <div className="mt-4">
          <Button variant="outline" size="sm">
            <Edit size={16} className="mr-2" />
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        {quickStats.map((stat, index) => (
          <Card key={index} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stat.color}`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{stat.label}</p>
                <p className="text-lg font-bold text-gray-900">
                  {stat.value}{stat.max ? `/${stat.max}` : ''}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Contact Information */}
      <Card className="p-4">
        <h3 className="text-md font-semibold text-gray-900 mb-3">Contact Information</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Mail size={16} className="text-gray-400" />
            <span className="text-sm text-gray-600">{user?.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Phone size={16} className="text-gray-400" />
            <span className="text-sm text-gray-600">{profile?.phone || 'Not provided'}</span>
          </div>
          <div className="flex items-center gap-3">
            <MapPin size={16} className="text-gray-400" />
            <span className="text-sm text-gray-600">{profile?.address || 'Not provided'}</span>
          </div>
          <div className="flex items-center gap-3">
            <Calendar size={16} className="text-gray-400" />
            <span className="text-sm text-gray-600">
              Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
        </div>
      </Card>

      {/* Module Activity */}
      <Card className="p-4">
        <h3 className="text-md font-semibold text-gray-900 mb-3">Module Activity</h3>
        <div className="grid grid-cols-2 gap-3">
          {moduleStats.map((module, index) => (
            <div key={index} className="flex items-center gap-3 p-2 rounded bg-gray-50">
              <div className={`p-2 rounded-lg ${module.color}`}>
                {module.icon}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{module.name}</p>
                <p className="text-xs text-gray-500">{module.count} items</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Profile Sections */}
      <div className="space-y-3">
        {profileSections.map((section) => (
          <Card
            key={section.id}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => console.log(`Navigate to ${section.id}`)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${section.color}`}>
                  {section.icon}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{section.title}</h4>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-400" />
            </div>
          </Card>
        ))}
      </div>

      {/* Account Actions */}
      <div className="space-y-3">
        <Button variant="outline" className="w-full justify-start">
          <Settings size={16} className="mr-2" />
          Settings
        </Button>
        <Button variant="outline" className="w-full justify-start">
          <HelpCircle size={16} className="mr-2" />
          Help & Support
        </Button>
        <Button 
          variant="outline" 
          className="w-full justify-start text-red-600 hover:text-red-700"
          onClick={handleSignOut}
        >
          <LogOut size={16} className="mr-2" />
          Sign Out
        </Button>
      </div>

      {/* App Version */}
      <div className="text-center">
        <p className="text-xs text-gray-400">iDecide Mobile v1.0.0</p>
      </div>
    </div>
  );
} 